/**
 * Hook for integrating cloned voices with the voice-cloning TTS provider.
 *
 * Provides cloned voices filtered by detected language, AND the real state of
 * the local model that has to speak them, so the UI can offer an install
 * instead of dead-ending.
 *
 * The provider a cloned voice is sent to is NOT asserted here any more: it
 * comes from VOICE_CLONING_TTS (constants/ttsSources.ts), the single place that
 * names both the backend TTS provider id and the installable component id. The
 * old code hardcoded `provider: 'f5_tts'` on every cloned voice and gated
 * availability on language alone, so a clone was offered and selected on a
 * machine where F5-TTS was not installed — the generation then failed mid-run
 * with no explanation and no path forward.
 */

import { useCallback, useMemo } from 'react';
import { useVoices } from './useVoices';
import { useLocalModelInstalls } from './useLocalModelInstalls';
import type { LocalModelInstallProgress, LocalModelRequirements } from './useLocalModelInstalls';
import type { IVoiceInfo } from '../types/voice.types';
import type { VoiceOption } from '../constants';
import { VOICE_CLONING_LANGUAGES, VOICE_CLONING_TTS } from '../constants/ttsSources';
import { ensureArray } from '../utils/apiNormalizers';

/** The one component id this hook tracks, as a stable array for the hook below. */
const VOICE_CLONING_COMPONENT_IDS: readonly string[] = [VOICE_CLONING_TTS.componentId];

/**
 * Maps ISO 639-1 language codes to TTS language codes.
 */
const DETECTED_LANG_TO_TTS_CODE: Record<string, string> = {
  en: 'en-us',
  pt: 'pt-br',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  zh: 'cmn',
  ru: 'ru',
  ar: 'ar',
  hi: 'hi',
  nl: 'nl',
  pl: 'pl',
  tr: 'tr',
  vi: 'vi',
  th: 'th',
  id: 'id',
  uk: 'uk',
};

/**
 * Languages supported for voice cloning with acceptable quality.
 *
 * The curated list itself is the backend's (VOICE_CLONING_LANGUAGES, from
 * `F5_TTS_VOICE_CLONING_LANGUAGES`): only English and Chinese produce
 * acceptable clones, Portuguese and Spanish were removed after testing.
 *
 * Expanded here with the TTS codes those same languages are addressed by in
 * this file's own DETECTED_LANG_TO_TTS_CODE map (zh -> cmn, en -> en-us), so a
 * lookup arriving as a TTS code still resolves to the language the backend
 * curated. The alias map is a frontend concern, so the expansion belongs here
 * and not in the constant.
 */
const VOICE_CLONING_SUPPORTED_LANGUAGES = new Set<string>(
  VOICE_CLONING_LANGUAGES.flatMap((language) => {
    const ttsCode = DETECTED_LANG_TO_TTS_CODE[language];
    return ttsCode ? [language, ttsCode] : [language];
  })
);

/**
 * Real state of the local model that speaks cloned voices on this machine.
 *
 * `stateKnown` is what makes `installed === false` actionable: the component
 * API answer is not in yet on the first render and never arrives at all in
 * serverless mode, so acting on `installed` before `stateKnown` would offer a
 * multi-GB download for a model that may already be on disk.
 */
export interface VoiceCloningModelState {
  /** Component id in the backend registry (utils/components/registry.py). */
  componentId: string;
  /** Display name for the install gate. */
  name: string;
  /** True when the component API reports the model as on disk and usable. */
  installed: boolean;
  /** False when this machine's GPU/RAM/disk can never meet the requirements. */
  compatible: boolean;
  /** Real requirements + download size, undefined until the API has answered. */
  requirements: LocalModelRequirements | undefined;
  /** Live install progress, present only while installing or after it finished/failed. */
  progress: LocalModelInstallProgress | undefined;
  /** True only once /api/v1/components has actually answered (desktop only). */
  stateKnown: boolean;
  /**
   * True when a cloned voice cannot be spoken here until the model is
   * installed AND this backend can actually install it — i.e. the state IS
   * known, the component exists on this build, and it is not on disk. The
   * single condition the install gate renders on.
   *
   * The "component exists" half matters: a backend that does not ship the
   * component returns no row for it, which is indistinguishable from "not
   * installed" if you only look at the installed-set. Offering the install
   * anyway produces a button whose POST 404s, forever, with a retry that can
   * never succeed — an install-shaped dead end in place of the generation
   * dead end this gate was built to remove.
   */
  needsInstall: boolean;
  /** Start (or retry) the REAL download; progress lands in `progress`. */
  install: () => void;
}

/**
 * Converts a cloned voice to a VoiceOption for TTS selection.
 */
function clonedVoiceToVoiceOption(voice: IVoiceInfo): VoiceOption {
  const voiceLang = getVoiceLanguageStatic(voice);
  return {
    id: `clone:${voice.voice_id}`,
    name: `${voice.name} (Cloned)`,
    gender: 'neutral' as const,
    language: DETECTED_LANG_TO_TTS_CODE[voiceLang] || voiceLang,
    provider: VOICE_CLONING_TTS.provider,
    isCloned: true,
    cloneVoiceId: voice.voice_id,
    audioUrl: voice.audio_url,
  } as VoiceOption & { isCloned: boolean; cloneVoiceId: string; audioUrl: string | null };
}

/**
 * Get effective language for a voice (detected_language or language_hint fallback).
 */
function getVoiceLanguageStatic(voice: IVoiceInfo): string {
  return voice.detected_language || voice.language_hint || 'en';
}

/** Whether the language itself is one voice cloning produces acceptable audio for. */
function isLanguageSupportedForCloning(language: string): boolean {
  const baseLanguage = language.split('-')[0].toLowerCase();
  return (
    VOICE_CLONING_SUPPORTED_LANGUAGES.has(baseLanguage) ||
    VOICE_CLONING_SUPPORTED_LANGUAGES.has(language.toLowerCase())
  );
}

/**
 * Hook for using cloned voices with the voice-cloning TTS provider.
 *
 * Returns cloned voices filtered by language, the state of the local model
 * needed to speak them, and helpers for deciding whether cloning is usable.
 */
export function useClonedVoicesForTTS() {
  const { voices: rawVoices, loading, error, fetchVoices } = useVoices();
  const {
    installedModels,
    availableModels,
    modelRequirements,
    installProgress,
    stateKnown,
    installModel,
  } = useLocalModelInstalls(VOICE_CLONING_COMPONENT_IDS);

  // Defensive: ensure voices is always an array (using centralized normalizer)
  const voices = ensureArray(rawVoices);

  const install = useCallback((): void => {
    void installModel(VOICE_CLONING_TTS.componentId).catch(() => {
      // Failure is already reflected in installProgress[componentId]
      // (stage: 'error') by the hook itself — nothing else to do here.
    });
  }, [installModel]);

  /**
   * Real installation state of the model behind cloned voices.
   */
  const cloningModel = useMemo<VoiceCloningModelState>(() => {
    const requirements = modelRequirements[VOICE_CLONING_TTS.componentId];
    const installed = installedModels.has(VOICE_CLONING_TTS.componentId);
    // Did this backend even report the component? A build without it returns
    // no row, which looks exactly like "not installed" from installedModels
    // alone — see needsInstall.
    const existsOnThisBackend = availableModels.has(VOICE_CLONING_TTS.componentId);
    return {
      componentId: VOICE_CLONING_TTS.componentId,
      name: VOICE_CLONING_TTS.name,
      installed,
      // Advisory, exactly like the SFX gate: when hardware detection itself
      // failed we do NOT claim the machine is inadequate — the backend
      // re-checks for real before it runs anything.
      compatible: requirements?.compatible ?? true,
      requirements,
      progress: installProgress[VOICE_CLONING_TTS.componentId],
      stateKnown,
      needsInstall: stateKnown && existsOnThisBackend && !installed,
      install,
    };
  }, [installedModels, availableModels, modelRequirements, installProgress, stateKnown, install]);

  /**
   * Get effective language for a voice (detected_language or language_hint fallback).
   */
  const getVoiceLanguage = (voice: IVoiceInfo): string | null => {
    return voice.detected_language || voice.language_hint || null;
  };

  /**
   * Languages that have at least one cloned voice (detected or hinted).
   */
  const availableClonedLanguages = useMemo(() => {
    const languages = new Set<string>();

    for (const voice of voices || []) {
      const lang = getVoiceLanguage(voice);
      if (lang) {
        languages.add(lang);
        // Also add the TTS code equivalent
        const ttsCode = DETECTED_LANG_TO_TTS_CODE[lang];
        if (ttsCode) {
          languages.add(ttsCode);
        }
      }
    }

    return languages;
  }, [voices]);

  /**
   * Check whether voice cloning can actually be used for a language RIGHT NOW.
   *
   * Two independent conditions, both required:
   *  1. the language is one cloning produces acceptable audio for (Portuguese
   *     and Spanish are NOT — see VOICE_CLONING_SUPPORTED_LANGUAGES);
   *  2. the provider that speaks the clone can run here — which on desktop
   *     means the local model is installed. This second half is what the hook
   *     used to ignore, reporting "available" for a model that was not on disk.
   *
   * While the install state is unknown (first render, or serverless mode where
   * there is nothing to install because the GPU worker runs it) this reports
   * available and lets the backend decide, mirroring the install gate.
   */
  const isVoiceCloningAvailableForLanguage = useCallback(
    (language: string): boolean => {
      if (!isLanguageSupportedForCloning(language)) return false;
      if (!cloningModel.stateKnown) return true;
      return cloningModel.installed && cloningModel.compatible;
    },
    [cloningModel]
  );

  /**
   * Get cloned voices for a specific language.
   * Only returns voices for languages with acceptable voice cloning quality.
   * Defensive: always returns an array (using centralized normalizer)
   */
  const getClonedVoicesForLanguage = useMemo(() => {
    return (language: string): IVoiceInfo[] => {
      const baseLanguage = language.split('-')[0].toLowerCase();

      // Only return cloned voices for supported languages
      if (!isLanguageSupportedForCloning(language)) {
        return [];
      }

      // Defensive: use ensureArray for safe iteration
      return ensureArray(voices).filter((voice) => {
        const voiceLang = getVoiceLanguage(voice);
        if (!voiceLang) return false;
        return (
          voiceLang === baseLanguage ||
          voiceLang === language ||
          DETECTED_LANG_TO_TTS_CODE[voiceLang] === language
        );
      });
    };
  }, [voices]);

  /**
   * Get cloned voices as VoiceOption array for TTS selection.
   *
   * The voices stay listed even when the local model is missing: they are the
   * user's own recordings and hiding them would be a silent dead end. The
   * install gate rendered next to the selector is what makes them usable.
   *
   * Defensive: always returns an array (using centralized normalizer)
   */
  const getClonedVoiceOptions = useMemo(() => {
    return (language: string): VoiceOption[] => {
      const clonedVoices = getClonedVoicesForLanguage(language);
      // Defensive: use ensureArray for safe iteration
      return ensureArray(clonedVoices).map(clonedVoiceToVoiceOption);
    };
  }, [getClonedVoicesForLanguage]);

  /**
   * Refresh cloned voices (with audio URLs for the cloning provider).
   */
  const refreshVoices = useMemo(() => {
    return async () => {
      await fetchVoices(true); // include_urls = true for the cloning provider
    };
  }, [fetchVoices]);

  return {
    /** All cloned voices */
    voices,
    /** Loading state */
    loading,
    /** Error message if any */
    error,
    /** Set of languages with cloned voices */
    availableClonedLanguages,
    /** TTS provider id a cloned voice is spoken by */
    voiceCloningProvider: VOICE_CLONING_TTS.provider,
    /** Real install state of the model behind cloned voices (drives the install gate) */
    cloningModel,
    /** Check if voice cloning is usable for a language on THIS machine */
    isVoiceCloningAvailableForLanguage,
    /** Get cloned voices for a specific language */
    getClonedVoicesForLanguage,
    /** Get cloned voices as VoiceOption for TTS dropdown */
    getClonedVoiceOptions,
    /** Refresh voices with audio URLs */
    refreshVoices,
  };
}

export default useClonedVoicesForTTS;
